import type { Ssm5Dtc, Ssm5DtcDescription } from '@packages/domain/ssm5';

import { colors } from '@packages/core/styles';
import { Button, Icon, TextBadge, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';

import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';
import { Ssm5TripTime } from './ssm5-trip-time';

/** The badge of a primary and of a secondary DTC. */
const KIND_BADGE = {
    primary: { label: 'ssm5:dtcs.badges.primary', variant: 'blue' },
    secondary: { label: 'ssm5:dtcs.badges.secondary', variant: 'gray' },
} as const;

/** The badge of a current and of a historical DTC. */
const STATUS_BADGE = {
    current: { label: 'ssm5:dtcs.badges.current', variant: 'redFive' },
    history: { label: 'ssm5:dtcs.badges.history', variant: 'yellowFive' },
} as const;

/** What shows in place of a description that is not reported, or cannot be shown. */
const DESCRIPTION_FALLBACK: Record<Exclude<Ssm5DtcDescription['state'], 'valid'>, string> = {
    empty: 'ssm5:dtcs.descriptionEmpty',
    invalid: 'ssm5:dtcs.descriptionInvalid',
};

const ACTION_ICON_SIZE = 24;

/** Props for {@link Ssm5DtcRow}. */
export type Ssm5DtcRowProps = {
    /** The DTC. */
    dtc: Ssm5Dtc;
    /** The FFD detail address of the DTC in its VIN session. */
    freezeFramesPath: string;
};

const useStyles = createUseStyles({
    // The button sits on the text line of the row: its padding hangs out of it, as in the design.
    actions: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginLeft: -4,
        minHeight: 24,
    },
    description: {
        alignItems: 'center',
        display: 'flex',
        minHeight: 24,
    },
    // The design underlines the link under the pointer. An underline takes the color of the block that sets it.
    freezeFramesLink: {
        '&:hover': {
            color: colors.blueLink,
            textDecoration: 'underline',
        },
        color: colors.blueLink,
        cursor: 'pointer',
    },
    heading: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        justifyContent: 'space-between',
    },
    identity: {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
});

/**
 * A DTC in its module card: its code, primary or secondary, current or historical, when it was set, its
 * description, its related documents and its freeze frame data. A description that is missing or
 * unreadable shows its fallback; the freeze frame link shows only when the DTC recorded some, without a
 * count when the count cannot be read. The documents button is drawn as the design has it, with the count
 * when one is reported; listing the documents comes with the related documents.
 * The link opens the FFD detail page of the DTC.
 *
 * @param props - {@link Ssm5DtcRowProps}
 * @returns the row
 */
export function Ssm5DtcRow({ dtc, freezeFramesPath }: Ssm5DtcRowProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const kindBadge = dtc.isSecondary ? KIND_BADGE.secondary : KIND_BADGE.primary;
    const statusBadge = dtc.isHistory ? STATUS_BADGE.history : STATUS_BADGE.current;
    const { freezeFrames, relatedDocumentCount } = dtc;

    return (
        <div className={classes.row}>
            <div className={classes.heading}>
                <div className={classes.identity}>
                    <Typography style={ssm5TextStyles.bodySemiboldTight} variant="default">
                        {dtc.code}
                    </Typography>
                    {[kindBadge, statusBadge].map((badge) => (
                        <TextBadge
                            key={badge.label}
                            style={ssm5ControlStyles.dtcBadge}
                            textStyle={ssm5TextStyles.dtcBadgeText}
                            variant={badge.variant}
                        >
                            {t(badge.label)}
                        </TextBadge>
                    ))}
                </div>
                {dtc.setAt && <Ssm5TripTime tripTime={dtc.setAt} />}
            </div>
            <div className={classes.description}>
                <Typography variant="caption">
                    {dtc.description.state === 'valid'
                        ? dtc.description.text
                        : t(DESCRIPTION_FALLBACK[dtc.description.state])}
                </Typography>
            </div>
            <div className={classes.actions}>
                <Button
                    iconRight={<Icon color="blueOne" name="arrow-small-down" size={ACTION_ICON_SIZE} />}
                    iconRightStyle={ssm5ControlStyles.dtcActionButtonIcon}
                    size="small"
                    style={ssm5ControlStyles.dtcActionButton}
                    title={
                        relatedDocumentCount === undefined
                            ? t('ssm5:dtcs.relatedDocumentsUncounted')
                            : t('ssm5:dtcs.relatedDocuments', { count: relatedDocumentCount })
                    }
                    titleStyle={ssm5TextStyles.smallButtonTitle}
                    variant="ghost-blue"
                />
                {freezeFrames.state !== 'none' && (
                    <Link to={freezeFramesPath}>
                        <span className={classes.freezeFramesLink}>
                            <Typography color="blueLink" style={ssm5TextStyles.link} variant="caption">
                                {freezeFrames.state === 'counted'
                                    ? t('ssm5:dtcs.freezeFrames', { count: freezeFrames.count })
                                    : t('ssm5:dtcs.freezeFramesUncounted')}
                            </Typography>
                        </span>
                    </Link>
                )}
            </div>
        </div>
    );
}
